import React from "react";

// Feather-style stroke icons copied from the design prototype (MyBooking-OlympicH-shareable.html)
// so the upsell pages render the exact same glyphs. All take colour from CSS `currentColor`.
const base = (size, strokeWidth = 2) => ({
    width: size, height: size, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth, strokeLinecap: "round", strokeLinejoin: "round",
});

export const ArrowLeftIcon = ({ size = 16 }) => (
    <svg {...base(size)}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
);

export const TrashIcon = ({ size = 15 }) => (
    <svg {...base(size)}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
);

export const CalendarIcon = ({ size = 15 }) => (
    <svg {...base(size)}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
);

export const PinIcon = ({ size = 15 }) => (
    <svg {...base(size)}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
);

export const CarIcon = ({ size = 15 }) => (
    <svg {...base(size)}><path d="M5 17h14M5 17a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h11l5 4v4a2 2 0 0 1-2 2" /><circle cx="7.5" cy="17.5" r="1.6" /><circle cx="16.5" cy="17.5" r="1.6" /></svg>
);

export const CartIcon = ({ size = 24, strokeWidth = 2 }) => (
    <svg {...base(size, strokeWidth)}><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
);

export const CheckIcon = ({ size = 14, strokeWidth = 3 }) => (
    <svg {...base(size, strokeWidth)}><polyline points="20 6 9 17 4 12" /></svg>
);

export const SteeringIcon = ({ size = 14 }) => (
    <svg {...base(size)}><circle cx="12" cy="12" r="3" /><path d="M3 12a9 9 0 0 1 18 0M5.6 7.6l3.3 3.3M18.4 7.6l-3.3 3.3M12 15v6" /></svg>
);

export const ClockIcon = ({ size = 15 }) => (
    <svg {...base(size)}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
);

export const InfoCircleIcon = ({ size = 20 }) => (
    <svg {...base(size)}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
);

export const PhoneIcon = ({ size = 15 }) => (
    <svg {...base(size)}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
);

export const StarIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

export const ChevronRightIcon = ({ size = 16, className = "" }) => (
    <svg {...base(size)} className={className}><polyline points="9 18 15 12 9 6" /></svg>
);

export const GlobeIcon = ({ size = 18 }) => (
    <svg {...base(size)}><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
);

export const CameraIcon = ({ size = 22 }) => (
    <svg {...base(size)}><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
);

// Car-hire extra glyphs, keyed on keywords in the supplier's extra description.
export const extraIconFor = (description) => {
    const d = (description || "").toLowerCase();
    const p = base(20);
    if (d.includes("gps") || d.includes("nav")) return <svg {...p}><path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>;
    if (d.includes("booster")) return <svg {...p}><path d="M6 4v10a3 3 0 0 0 3 3h7" /><path d="M6 9h7" /><path d="M16 17l2 3" /></svg>;
    if (d.includes("child") || d.includes("baby") || d.includes("infant") || d.includes("seat")) return <svg {...p}><circle cx="12" cy="5" r="2" /><path d="M9 9h6l1 5h-2v6h-4v-6H8z" /></svg>;
    if (d.includes("driver")) return <svg {...p}><circle cx="12" cy="8" r="3.2" /><path d="M5 20a7 7 0 0 1 14 0" /></svg>;
    if (d.includes("wifi") || d.includes("wi-fi")) return <svg {...p}><path d="M5 12.5a10 10 0 0 1 14 0" /><path d="M8.5 16a5 5 0 0 1 7 0" /><line x1="12" y1="19.5" x2="12.01" y2="19.5" /></svg>;
    if (d.includes("snow") || d.includes("chain")) return <svg {...p}><line x1="12" y1="3" x2="12" y2="21" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="5.6" y1="5.6" x2="18.4" y2="18.4" /><line x1="18.4" y1="5.6" x2="5.6" y2="18.4" /></svg>;
    return <svg {...p}><circle cx="12" cy="12" r="9" /></svg>;
};
